/* eslint-disable jsx-a11y/role-supports-aria-props */
import type { ReactElement, Ref } from 'react';

import { useNavigate, useSearch } from '@tanstack/react-router';
import type { Row, Table } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import clsx from 'clsx';

import type { FleetMemberRow } from 'src/api/endpoints/cloudEnterprise.types';
import { TableCell } from 'src/components/Table';
import { useKeyboardNavigation } from 'src/utils/useKeyboardNavigation';

import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { fleetMemberRowHelpers } from './fleetMemberRowHelpers';
import { TableDrawer } from './TableDrawer';

interface TableRowProps<T> {
  row: Row<T>;
  table: Table<T>;
}

type TableRowRef = Ref<HTMLTableRowElement>;

function TableRowImpl<T extends FleetMemberRow>({
  row,
  table,
  ref,
}: TableRowProps<T> & { ref?: TableRowRef }) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const isActiveRow = search.member === row.original.data.email;
  const isDeactivatedMemberRow =
    'status' in row.original.data && row.original.data.status === 'deactivated';

  const isMemberMarkedForDeletion =
    'status' in row.original.data &&
    row.original.data.status === 'deletion_mark';
  const isMdmDeactivatedMemberRow = fleetMemberRowHelpers.isMdmDeactivated(
    row.original
  );
  const openModal = () =>
    navigate({
      to: '.',
      search: { ...search, member: row.original.data.email },
      replace: true,
    });

  const closeModal = () =>
    navigate({
      to: '.',
      search: { ...search, member: undefined, devices: undefined },
      replace: true,
    });

  return (
    <TableDrawer
      row={row.original}
      open={isActiveRow}
      onOpenChange={(open) => {
        if (open) {
          void openModal();
        } else {
          void closeModal();
        }
      }}
    >
      <tr
        data-cy={row.id}
        key={row.id}
        className={clsx('group table-row', {
          'bg-paper-4 hover:bg-paper-5': isActiveRow,
          'opacity-50': isDeactivatedMemberRow || isMemberMarkedForDeletion,
          'bg-error-subtle': isMdmDeactivatedMemberRow,
        })}
        ref={ref}
        tabIndex={0}
        role="button"
        aria-label={table.options.meta?.getRowLabel?.(row.original)}
        aria-description={table.options.meta?.getRowDescription?.(row.original)}
        onClick={() => {
          tracker.trackEvent(
            new ButtonClicked({
              component_location: ComponentLocations.ENTERPRISE.MEMBERS_PAGE,
              text: '[member table row]',
              action: 'open member details drawer',
            })
          );
        }}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            data-cy={cell.id}
            truncate={cell.column.columnDef.meta?.truncate}
            align={cell.column.columnDef.meta?.align}
            className={clsx(cell.column.columnDef.meta?.colWidth)}
            fullWidth={cell.column.columnDef.meta?.fullWidth}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </tr>
    </TableDrawer>
  );
}

const TableRow = TableRowImpl as <T>(
  props: TableRowProps<T> & { ref?: TableRowRef }
) => ReactElement;

export function FleetTableBody<T>({ table }: { table: Table<T> }) {
  const rowModel = table.getRowModel();
  const { getRef } = useKeyboardNavigation();

  return (
    <>
      {rowModel.rows.map((row, index) => (
        <TableRow
          table={table}
          row={row}
          key={row.id}
          ref={(el) => getRef(index, el)}
        />
      ))}
    </>
  );
}
